/* Copyright (c) 2020-present CloudIO Inc */

import { ActionItemType } from 'componentregistry/registry/Base.type';
import { Platform } from '../../platform/Platform';
import {
  ActionManager,
  ActionManagerFn,
  AliasType,
} from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionQuerySelect';
import { analyzeActionQuerySelect } from './ActionQuerySelectAnalyze';

const ActionQuerySelectManager: ActionManagerFn<'ActionQuerySelect'> = (
  platform: Platform,
): ActionManager<'ActionQuerySelect'> => {
  return {
    ...baseActionManager(platform, 'ActionQuerySelect'),
    defaultValue,
    props: {
      ...baseActionProps,
      selectItemId: {
        label: 'Select Item ID',
        type: 'ItemID',
        initialFilter: 'Select',
        required: true,
        validateItemId: (value, { pageId, appUid, itemId }) => {
          const item = platform.getItem(appUid, pageId, value);
          if (item?.type !== 'Select' && item?.type !== 'SelectColumn') {
            return `Item id '${value}' doesn't belong to a Select component.`;
          }
          return '';
        },
      },
      showEmptyFilterWarning: {
        label: 'Show Empty Filter Warning',
        type: 'boolean',
        helpText:
          'Enable this to know if empty value passed to filters(Recommended for developer).',
      },
    },
    isChildAllowed: (page, item, childType) =>
      (
        [
          'ActionSetFilterCombiner',
          'ActionSetQueryFilter',
          'ActionSetSimpleQueryFilter',
        ] as ActionItemType[]
      ).includes(childType as ActionItemType),
    getDataSourceAliasProperty: (aliasType: AliasType) => {
      return aliasType === 'QUERY_FILTER'
        ? {
            aliasProp: 'comboDatasourceAlias',
            embeddedPageIdProp: 'embeddedPageId',
          }
        : undefined;
    },
    getDisplayText: (item) => item.props.selectItemId,
    name: 'Query Select',
    analyze: analyzeActionQuerySelect,
    icon: { iconName: 'search' },
    description: `**Overview**<br/>
The Query Select action is a data management component designed to programmatically refresh the options list of a "Select" (dropdown) or "Select Column" component. It functions as the mechanism for implementing dynamic or cascading lists, allowing the application to reload the available choices in a dropdown based on new criteria without reloading the entire page.<br/><br/>
This action serves as a wrapper for the data fetching logic tied specifically to input selection widgets. It acts as a parent container for filter-defining child actions—ActionSetQueryFilter, ActionSetSimpleQueryFilter, and ActionSetFilterCombiner—enabling developers to apply strict constraints to the data query before the dropdown creates its list of options.

**Key Features**<br/>
* **Targeted Refresh:** Specifically validates selectItemId to ensure it only targets Select or SelectColumn components, preventing configuration errors.
* **Cascading Logic:** Enables the creation of dependent fields (e.g., selecting a Country refreshes the State dropdown) by allowing filters to be injected dynamically into the query.
* **Ad-Hoc Filtering:** Supports child components that define temporary SQL-like constraints (e.g., WHERE Category = 'Hardware') applied only to this specific data fetch operation.
* **Developer Diagnostics:** Features a showEmptyFilterWarning flag to help debug scenarios where filters might be accidentally passing null or undefined values during development.

**Properties**<br/>
* **Select Item ID (selectItemId) (Required):** A property of type ItemID.
  * *Function:* Identifies the specific Select or SelectColumn component to update.
  * *Validation:* Must reference a valid Select component.
* **Show Empty Filter Warning:** A boolean property.
  * *Function:* If enabled, the system logs a warning if the query is executed with empty filter values, helping identify logic gaps in filter expressions.

**Use Cases**<br/>
* **Cascading Dropdowns:** When a user selects "USA" in a Country dropdown, this action triggers to query the State dropdown, applying a filter CountryID = SelectedCountryID.
* **Search-As-You-Type:** Triggering this action on a debounce timer when a user types into a search box to fetch relevant options from the server.
* **Contextual Options:** Reloading a "Assign to User" dropdown to only show users who belong to the department selected in a different form field.`,
  };
};

export default ActionQuerySelectManager;
